import { useEffect, useRef, useState } from "react";
import "@/App.css";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import Lenis from "lenis";
import { BrowserRouter, Routes, Route, Link } from "react-router-dom";
import { ArrowRight, ArrowDown, Plus, Minus, Star } from "lucide-react";

const STRIPE_FULL_URL = "https://buy.stripe.com/bJe5kD3pTeK62DX9RY5c410";
const STRIPE_SPLIT_URL = "https://buy.stripe.com/5kQeVd7G96dAemF3tA5c411";
const CALENDLY_URL = "https://calendly.com/persephonedennis/working-with-persephone";

const track = (event, label = "") => {
  try {
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/track`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ event, label }),
      keepalive: true,
    });
  } catch {}
};

const PULSE_LABELS = {
  "pageview:/": "Page visits",
  "pageview:/thank-you": "Thank-you page visits (buyers)",
  "click:hero-full-444": "Hero · Join for £444",
  "click:hero-split-222": "Hero · 2 × £222",
  "click:pricing-full-444": "Pricing · Pay £444 in full",
  "click:pricing-split-222": "Pricing · Pay in 2 × £222",
  "click:final-full-444": "Final invitation · £444",
  "click:final-split-222": "Final invitation · 2 × £222",
  "click:hero-book-call": "Hero · Book a call",
  "click:pricing-book-call": "Pricing · Book a call",
  "click:final-book-call": "Final invitation · Book a call",
  "click:nav-join": "Top bar · Join",
  "click:bonus-join": "Letter · Join The Reset",
};

const EASE = [0.22, 1, 0.36, 1];

const Reveal = ({ children, delay = 0, className = "" }) => (
  <motion.div
    className={className}
    initial={{ opacity: 0, y: 36 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-60px" }}
    transition={{ duration: 0.9, delay, ease: EASE }}
  >
    {children}
  </motion.div>
);

const Overline = ({ children, light = false }) => (
  <div className="flex items-center gap-4 mb-8">
    <span className="h-px w-10 bg-sienna" />
    <span className={`text-xs tracking-[0.28em] uppercase font-medium ${light ? "text-ash" : "text-sienna"}`}>
      {children}
    </span>
  </div>
);

const PayButtons = ({ align = "start", idPrefix }) => (
  <div className={`flex flex-col sm:flex-row gap-4 ${align === "center" ? "justify-center" : ""}`}>
    <motion.a
      href={STRIPE_FULL_URL}
      data-testid={`${idPrefix}-cta-full-pay`}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.25 }}
      className="group inline-flex items-center justify-center gap-3 bg-sienna text-bone px-8 py-4 text-sm tracking-[0.12em] uppercase font-semibold hover:bg-ember transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-sienna focus:ring-offset-2 focus:ring-offset-obsidian"
    >
      Join for £444 / $597.78
      <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
    </motion.a>
    <motion.a
      href={STRIPE_SPLIT_URL}
      data-testid={`${idPrefix}-cta-split-pay`}
      onClick={() => track("click", `${idPrefix}-split-222`)}
      whileHover={{ y: -2 }}
      whileTap={{ scale: 0.98 }}
      transition={{ duration: 0.25 }}
      className="inline-flex items-center justify-center gap-3 border border-white/20 text-bone px-8 py-4 text-sm tracking-[0.12em] uppercase font-medium hover:bg-white/5 hover:border-white/40 transition-colors duration-300 focus:outline-none focus:ring-2 focus:ring-sienna focus:ring-offset-2 focus:ring-offset-obsidian"
    >
      Or 2 × £222 / $298.89
    </motion.a>
  </div>
);

const BookCallLink = ({ idPrefix }) => (
  <a
    href={CALENDLY_URL}
    target="_blank"
    rel="noopener noreferrer"
    data-testid={`${idPrefix}-book-call-link`}
    onClick={() => track("click", `${idPrefix}-book-call`)}
    className="group inline-flex items-center gap-2 text-sm text-ash hover:text-bone transition-colors duration-300"
  >
    <span>Prefer to talk it through first?</span>
    <span className="link-underline text-bone group-hover:text-sienna transition-colors duration-300">
      Book a call with me
    </span>
    <ArrowRight className="w-3.5 h-3.5 text-sienna transition-transform duration-300 group-hover:translate-x-1" />
  </a>
);

const Nav = () => {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 transition-colors duration-500 ${
        scrolled ? "bg-obsidian/90 backdrop-blur-md border-b border-white/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between">
        <span className="font-display text-lg tracking-wide text-bone">Persephone Dennis</span>
        <a
          href={STRIPE_FULL_URL}
          data-testid="nav-join-btn"
          onClick={() => track("click", "nav-join")}
          className="link-underline text-xs tracking-[0.2em] uppercase font-semibold text-bone hover:text-sienna transition-colors duration-300"
        >
          Join · £444 / $597.78
        </a>
      </div>
    </header>
  );
};

const HeroLine = ({ children, delay, className = "" }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className={`block ${className}`}
      initial={{ y: "115%" }}
      animate={{ y: 0 }}
      transition={{ duration: 1.1, delay, ease: EASE }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const glowY = useTransform(scrollYProgress, [0, 1], [0, 180]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section ref={ref} className="relative min-h-screen flex flex-col justify-center overflow-hidden pt-24 pb-16">
      <motion.div style={{ y: glowY }} className="hero-glow absolute inset-0 pointer-events-none" />
      <motion.div style={{ y: textY }} className="relative max-w-6xl mx-auto px-6 md:px-10 w-full">
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.8, delay: 0.1 }}
        >
          <Overline>The Reset · a six-week live coaching experience</Overline>
        </motion.div>
        <h1 className="font-display font-light text-5xl sm:text-6xl lg:text-7xl xl:text-[5.5rem] leading-[1.04] tracking-tight text-bone max-w-5xl">
          <HeroLine delay={0.2}>You already know</HeroLine>
          <HeroLine delay={0.32}>what needs to change.</HeroLine>
          <HeroLine delay={0.44} className="italic text-sienna">
            The question is who
          </HeroLine>
          <HeroLine delay={0.56}>you're willing to become.</HeroLine>
        </h1>
        <motion.p
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 0.85, ease: EASE }}
          className="mt-10 max-w-xl text-base md:text-lg leading-relaxed text-ash"
        >
          This is not another course, and there are no modules to complete. It is six weeks of live
          group coaching with me, a small, interactive room, designed to do one thing: create
          genuine movement in the one area of your life that has stayed stuck, no matter how much
          you already understand about it.
        </motion.p>
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.9, delay: 1, ease: EASE }}
          className="mt-10"
        >
          <PayButtons idPrefix="hero" />
          <p className="mt-5 text-xs tracking-[0.15em] uppercase text-ash/70">
            Now enrolling · Live Mondays 8:30pm UK / 3:30pm EST · We begin Monday 24th August · Small group
          </p>
          <div className="mt-7">
            <BookCallLink idPrefix="hero" />
          </div>
        </motion.div>
      </motion.div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6, duration: 1 }}
        className="absolute bottom-8 left-6 md:left-10 flex items-center gap-3 text-ash/60"
      >
        <ArrowDown className="w-4 h-4 animate-bounce" />
        <span className="text-xs tracking-[0.25em] uppercase">Read on</span>
      </motion.div>
    </section>
  );
};

const Opening = () => (
  <section className="py-28 md:py-36">
    <div className="max-w-3xl mx-auto px-6 md:px-10">
      <Reveal>
        <p className="font-display font-light text-2xl md:text-[2rem] leading-relaxed text-bone">
          There is a particular kind of frustration that comes from knowing better. You have read
          the books. Listened to the podcasts. Highlighted the passages that felt like they were
          written about you. And yet, if you are honest with yourself, the same patterns keep
          quietly running your life: the same hesitation at the moment of decision, the same
          negotiation with yourself, the same gap between who you know you are and how you are
          actually living.
        </p>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-10 text-base md:text-lg leading-relaxed text-ash">
          I want you to know something before we go any further: nothing is wrong with you. You are
          not broken, and you are not behind. You have simply been trying to change your life at
          the level of behaviour, and change was never going to happen there.
        </p>
      </Reveal>
    </div>
  </section>
);

const Problem = () => (
  <section className="py-24 md:py-32 bg-coal border-y border-white/10">
    <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
      <Reveal className="md:col-span-5">
        <Overline>Why you stay stuck</Overline>
        <h2 className="font-display font-light text-4xl md:text-5xl leading-tight text-bone">
          Behaviour is the last domino, not the first.
        </h2>
      </Reveal>
      <Reveal delay={0.15} className="md:col-span-7 space-y-6 text-base md:text-lg leading-relaxed text-ash">
        <p>
          Most of us were taught that change is a matter of effort, that if we just tried harder,
          planned better, or wanted it enough, we would finally follow through. But behaviour only
          falls because something upstream moved it: your identity. The quiet, mostly unconscious
          story you hold about who you are, what you deserve, and what is possible for someone like
          you.
        </p>
        <p>
          That story was written in the subconscious mind, reinforced by years of repetition, and
          protected by a nervous system that prefers the familiar to the fulfilling. This is why you
          can know exactly what to do and still not do it. Knowing lives in the conscious mind.
          Living happens much deeper.
        </p>
        <p className="text-bone font-medium">
          Lasting change never comes from changing behaviour alone. It comes from becoming a
          different person. When identity shifts, behaviour follows, without the white-knuckling,
          without the Sunday-night promises.
        </p>
      </Reveal>
    </div>
  </section>
);

const Marquee = () => {
  const words = ["identity", "the subconscious mind", "neuroplasticity", "human behaviour", "emotional awareness", "becoming"];
  const row = [...words, ...words];
  return (
    <div data-testid="editorial-marquee" className="py-10 border-b border-white/10 overflow-hidden">
      <div className="animate-marquee flex w-max items-center">
        {[0, 1].map((half) => (
          <div key={half} className="flex items-center shrink-0">
            {row.map((w, i) => (
              <span key={`${half}-${i}`} className="flex items-center">
                <span className="font-display italic font-light text-3xl md:text-4xl text-bone/50 px-8 whitespace-nowrap">
                  {w}
                </span>
                <span className="w-1.5 h-1.5 rounded-full bg-sienna/60" />
              </span>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
};

const CHAPTERS = [
  {
    num: "01",
    title: "Change the person, not the plan.",
    body: "You have tried new plans before. Better mornings, stricter calendars, fresh starts on Mondays. The plan was never the problem. When the identity beneath your behaviour shifts, the behaviour reorganises itself around it, naturally and without force. That is the only order of operations that lasts.",
  },
  {
    num: "02",
    title: "Live coaching, not more content.",
    body: "You do not need another module to complete or another theory to admire. You need someone to sit with you, in real time, while you meet the actual belief, fear, or pattern that has been running the show. That is what we do together, every week, for six weeks.",
  },
  {
    num: "03",
    title: "Your brain is on your side.",
    body: "The mind you have today is not the one you are stuck with. Neuroplasticity means that with the right attention, repetition and emotional engagement, new ways of being stop being ideas and start becoming defaults. We work with that process deliberately, week by week.",
  },
];

const Manifesto = () => (
  <section className="py-24 md:py-32">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <Reveal>
        <Overline>Why this is different</Overline>
        <h2 className="font-display font-light text-4xl md:text-5xl leading-tight text-bone max-w-2xl mb-20">
          Three things I know to be true.
        </h2>
      </Reveal>
      <div className="space-y-0">
        {CHAPTERS.map((c, i) => (
          <Reveal key={c.num} delay={0.05 * i}>
            <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-10 py-12 border-t border-white/10">
              <div className="md:col-span-2">
                <span className="font-display text-5xl md:text-6xl font-light text-sienna/80">{c.num}</span>
              </div>
              <div className="md:col-span-4">
                <h3 className="font-display font-light text-2xl md:text-3xl leading-snug text-bone">{c.title}</h3>
              </div>
              <div className="md:col-span-6">
                <p className="text-base leading-relaxed text-ash">{c.body}</p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WEEKS = [
  { num: "Week one", title: "Telling the truth", body: "We begin where you actually are. Where are you genuinely stuck, and what has staying there been protecting you from?", span: "md:col-span-7" },
  { num: "Week two", title: "The identity beneath the pattern", body: "You will meet the story that has been quietly making your decisions for you, often for the first time.", span: "md:col-span-5" },
  { num: "Week three", title: "The subconscious and emotional awareness", body: "Why insight alone has never been enough, and how change actually takes root beneath the surface.", span: "md:col-span-5" },
  { num: "Week four", title: "Becoming", body: "Practising the new identity in real life, with the group holding you steady while it still feels unfamiliar.", span: "md:col-span-7" },
  { num: "Week five", title: "Resistance", body: "What happens when the old self fights back, and how to keep moving anyway.", span: "md:col-span-7" },
  { num: "Week six", title: "Integration", body: "Consolidating the shift, and building the practices that carry it forward long after we finish.", span: "md:col-span-5" },
];

const Weeks = () => (
  <section className="py-24 md:py-32 bg-coal border-y border-white/10">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <Reveal>
        <Overline>The six weeks</Overline>
        <h2 className="font-display font-light text-4xl md:text-5xl leading-tight text-bone max-w-3xl">
          One live session each week. A small group, deliberately. You will be coached, not lectured at.
        </h2>
        <p className="mt-6 max-w-2xl text-base leading-relaxed text-ash">
          We gather live every Monday at 8:30pm UK time (3:30pm EST), beginning Monday 24th
          August. Sessions are interactive: real coaching, real accountability, real support. Every session is
          recorded, so missing one never means falling behind.
        </p>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-4">
        {WEEKS.map((w, i) => (
          <Reveal key={w.num} delay={0.05 * i} className={w.span}>
            <div className="group h-full border border-white/10 bg-obsidian p-8 hover:border-sienna/50 transition-colors duration-500">
              <span className="text-xs tracking-[0.25em] uppercase text-sienna">{w.num}</span>
              <h3 className="mt-4 font-display font-light text-2xl md:text-3xl text-bone">{w.title}</h3>
              <p className="mt-4 text-sm md:text-base leading-relaxed text-ash">{w.body}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const TAKEAWAYS = [
  "Clarity on the one area of your life that has resisted change, and an honest understanding of why",
  "A lived experience of moving through something you previously only thought about",
  "Practical tools drawn from identity work, the subconscious mind and neuroplasticity, yours to keep using",
  "A more honest, more compassionate relationship with yourself",
  "The momentum that comes from finally being in motion, with people who saw you do it",
];

const WalkAway = () => (
  <section className="py-24 md:py-32">
    <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16">
      <Reveal className="md:col-span-5">
        <Overline>What you walk away with</Overline>
        <h2 className="font-display font-light text-4xl md:text-5xl leading-tight text-bone">
          I am not going to promise you everything.
        </h2>
        <p className="mt-6 text-base leading-relaxed text-ash">
          Six weeks will not solve every problem you have, and anyone who tells you otherwise is
          selling you something. What six weeks of focused coaching can do is create the momentum,
          clarity and identity shifts that many people have been unable to create alone for years.
          That is the promise I am willing to make, and to keep.
        </p>
      </Reveal>
      <div className="md:col-span-7">
        <ul className="space-y-0">
          {TAKEAWAYS.map((t, i) => (
            <Reveal key={i} delay={0.06 * i}>
              <li className="flex gap-5 py-6 border-t border-white/10 last:border-b">
                <span className="mt-1.5 w-2 h-2 rounded-full bg-sienna shrink-0" />
                <span className="text-base md:text-lg leading-relaxed text-bone/90">{t}</span>
              </li>
            </Reveal>
          ))}
        </ul>
        <Reveal delay={0.3}>
          <p className="mt-8 text-base leading-relaxed text-ash">
            By the end you should feel clearer, more confident, more self-aware, more decisive,
            and more connected to yourself than you have in a long time. Less stuck. More hopeful.
            Equipped.
          </p>
        </Reveal>
      </div>
    </div>
  </section>
);

const FOR = [
  "You know you are capable of far more than you are currently living",
  "You know what to do, you're just not consistently doing it, and it frustrates you",
  "You've invested in personal development before and still feel stuck",
  "You've read the books, listened to the podcasts, watched the videos, and lasting change hasn't followed",
  "You feel like you're repeating the same patterns",
  "You want more confidence, freedom, fulfilment or success, and can't see why you keep holding yourself back",
  "You feel disconnected from who you really are",
  "You want support, accountability and expert coaching, and you're willing to do the work",
];

const NOT_FOR = [
  "You're looking for a quick fix",
  "You want motivation without responsibility",
  "You're unwilling to be challenged",
  "You expect transformation without action",
];

const WhoFor = () => (
  <section className="py-24 md:py-32 bg-coal border-y border-white/10">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <Reveal>
        <Overline>An honest filter</Overline>
        <h2 className="font-display font-light text-4xl md:text-5xl leading-tight text-bone max-w-2xl mb-16">
          This is for you if, and only if.
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Reveal>
          <div className="h-full border border-white/10 bg-obsidian p-8 md:p-10">
            <h3 className="text-xs tracking-[0.25em] uppercase text-sienna font-semibold mb-8">This is for you if</h3>
            <ul className="space-y-5">
              {FOR.map((f, i) => (
                <li key={i} className="flex gap-4 text-sm md:text-base leading-relaxed text-bone/90">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-sienna shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="h-full border border-white/10 bg-obsidian p-8 md:p-10">
            <h3 className="text-xs tracking-[0.25em] uppercase text-ash font-semibold mb-8">This is not for you if</h3>
            <ul className="space-y-5">
              {NOT_FOR.map((f, i) => (
                <li key={i} className="flex gap-4 text-sm md:text-base leading-relaxed text-ash">
                  <span className="mt-2 w-1.5 h-1.5 rounded-full bg-charcoal shrink-0" />
                  {f}
                </li>
              ))}
            </ul>
            <p className="mt-10 text-sm leading-relaxed text-ash/70 italic font-display text-lg">
              I'd rather you felt the honesty of that now than the regret of it later.
            </p>
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Pricing = () => (
  <section id="join" className="py-24 md:py-32 scroll-mt-20">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <Reveal>
        <Overline>Joining us</Overline>
        <h2 className="font-display font-light text-4xl md:text-5xl leading-tight text-bone max-w-2xl">
          Two doors. The same room.
        </h2>
        <p className="mt-6 max-w-xl text-base leading-relaxed text-ash">
          The Reset is £444. If it's easier to spread that across two months, you can join for two
          payments of £222 instead. Nothing changes about what you receive: the same six weeks,
          the same coaching, the same seat in the room.
        </p>
      </Reveal>
      <div className="mt-14 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Reveal>
          <div className="h-full border border-sienna/40 bg-coal p-8 md:p-10 flex flex-col">
            <span className="text-xs tracking-[0.25em] uppercase text-sienna font-semibold">One payment</span>
            <div className="mt-6 flex items-end gap-2">
              <span className="font-display font-light text-6xl text-bone">£444</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-ash flex-1">
              Paid in full, and it's done. Six weeks of live group coaching, all session recordings,
              and a place in a deliberately small room.
            </p>
            <motion.a
              href={STRIPE_FULL_URL}
              data-testid="pricing-cta-full-pay"
              onClick={() => track("click", "pricing-full-444")}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="group mt-8 inline-flex items-center justify-center gap-3 bg-sienna text-bone px-8 py-4 text-sm tracking-[0.12em] uppercase font-semibold hover:bg-ember transition-colors duration-300"
            >
              Pay £444 / $597.78 in full
              <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
            </motion.a>
          </div>
        </Reveal>
        <Reveal delay={0.15}>
          <div className="h-full border border-white/10 bg-coal p-8 md:p-10 flex flex-col">
            <span className="text-xs tracking-[0.25em] uppercase text-ash font-semibold">Two payments</span>
            <div className="mt-6 flex items-end gap-3">
              <span className="font-display font-light text-6xl text-bone">£222</span>
              <span className="text-ash text-sm pb-2">× 2 months</span>
            </div>
            <p className="mt-4 text-sm leading-relaxed text-ash flex-1">
              The same experience, spread across two monthly payments. £222 today, £222 next month.
            </p>
            <motion.a
              href={STRIPE_SPLIT_URL}
              data-testid="pricing-cta-split-pay"
              onClick={() => track("click", "pricing-split-222")}
              whileHover={{ y: -2 }}
              whileTap={{ scale: 0.98 }}
              className="mt-8 inline-flex items-center justify-center gap-3 border border-white/20 text-bone px-8 py-4 text-sm tracking-[0.12em] uppercase font-medium hover:bg-white/5 hover:border-white/40 transition-colors duration-300"
            >
              Pay 2 × £222 / $298.89
            </motion.a>
          </div>
        </Reveal>
        <Reveal delay={0.2}>
          <p data-testid="pricing-currency-note" className="mt-8 text-sm leading-relaxed text-ash/80 max-w-xl">
            One honest note: checkout is in US dollars, $597.78 in full or two payments of
            $298.89, which carries the same £444 across the ocean, wherever you are in the
            world. Nothing else about the offer changes.
          </p>
        </Reveal>
        <Reveal delay={0.25}>
          <div className="mt-10">
            <BookCallLink idPrefix="pricing" />
          </div>
        </Reveal>
      </div>
    </div>
  </section>
);

const Bonus = () => (
  <section className="relative py-24 md:py-32 border-y border-white/10 overflow-hidden">
    <div
      className="absolute inset-0 bg-cover bg-center opacity-20"
      style={{ backgroundImage: "url('https://images.unsplash.com/photo-1631832612525-98ba50f35189?auto=format&fit=crop&w=1600&q=70')" }}
    />
    <div className="bonus-glow absolute inset-0" />
    <div className="relative max-w-3xl mx-auto px-6 md:px-10">
      <Reveal>
        <Overline>A birthday gift</Overline>
        <h2 className="font-display font-light text-4xl md:text-5xl leading-tight text-bone">
          I have never done this before.
        </h2>
        <div className="mt-8 space-y-6 text-base md:text-lg leading-relaxed text-bone/85">
          <p>Hello,</p>
          <p>
            I wasn't planning to offer this. Six weeks, a small group, my live coaching, and on
            top of it all a private session with me, gifted. It arrived this week as a pull
            rather than a plan, and I have learned to trust those.
          </p>
          <p>
            Here is the honest reason behind it. It is my birthday week, and birthdays make me
            reflective. I kept thinking about the people who are ready. Not the ones still
            gathering information, but the ones who already know something has to change and
            simply haven't had the right room to change it in. I realised I wanted fewer of you
            watching from a distance, and more of you sitting across from me, doing the work.
          </p>
          <p>
            So this is me, asking. If you join The Reset by the end of Tuesday 11th August, a
            complimentary private 1:1 coaching session with me is yours. A full hour, just the
            two of us, whenever you need it most during our six weeks together. It isn't a
            tactic. It's a gift, because gifts are what birthdays are for, and because I
            genuinely want to meet the people who are ready.
          </p>
          <p>
            Whatever you decide, thank you for reading this far. It means more than you know.
          </p>
        </div>
        <p className="mt-10 font-display italic font-light text-3xl md:text-4xl text-bone">
          Persephone
        </p>
        <p className="mt-10 text-xs tracking-[0.2em] uppercase text-sienna font-semibold">
          Join by end of day, Tuesday 11th August
        </p>
        <div className="mt-6">
          <a
            href="#join"
            data-testid="bonus-join-link"
            onClick={() => track("click", "bonus-join")}
            className="link-underline text-sm text-bone hover:text-sienna transition-colors duration-300"
          >
            Join The Reset
          </a>
        </div>
      </Reveal>
    </div>
  </section>
);

const TESTIMONIALS = [
  { quote: "I'd tried everything before joining the group, but nothing seemed to create lasting change. Then I found more clarity than I'd found in years. Persephone has a way of asking the questions you've been avoiding your whole life. This wasn't just coaching. It completely changed the way I see myself.", name: "Sarah Mitchell", stars: true },
  { quote: "I almost didn't join because I thought I could work everything out on my own. I'm so glad I didn't. The combination of live coaching, accountability and being surrounded by people on the same journey gave me the momentum I'd been missing. Since then I've made decisions I'd been putting off for years.", name: "James Carter", stars: true },
  { quote: "The biggest breakthrough wasn't achieving my goal. It was realising I'd been the one standing in my own way. Persephone helped me uncover beliefs I didn't even know I had. The group was supportive, challenging and unlike any coaching experience I've ever had. I'd recommend it to anyone who knows they're capable of more.", name: "Emma Richardson", stars: true },
  { quote: "I don't even know how to explain it... I just feel like I've got myself back. I came because I felt stuck, but I've left with so much clarity and confidence. Worth every penny.", name: "Katie W.", stars: false },
  { quote: "I thought group coaching would mean I'd get less attention than one-to-one, but it was actually the opposite. Watching other people get coached helped me realise things about myself I never would've seen otherwise.", name: "Mark H.", stars: false },
  { quote: "The time flew by. I made more progress than I had in the previous six months on my own. If you're thinking about joining, stop thinking and just do it.", name: "Louise P.", stars: false },
];

const Testimonials = () => (
  <section className="py-24 md:py-32">
    <div className="max-w-6xl mx-auto px-6 md:px-10">
      <Reveal>
        <Overline>In their words</Overline>
        <h2 className="font-display font-light text-4xl md:text-5xl leading-tight text-bone mb-14">
          Love letters about my group coaching.
        </h2>
      </Reveal>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {TESTIMONIALS.map((t, i) => (
          <Reveal key={i} delay={0.08 * i}>
            <figure className="h-full border border-white/10 bg-coal p-8 flex flex-col">
              <blockquote className="font-display font-light italic text-lg md:text-xl leading-relaxed text-bone/90 flex-1">
                “{t.quote}”
              </blockquote>
              <figcaption className="mt-8">
                {t.stars && (
                  <span className="flex gap-1 mb-3">
                    {[...Array(5)].map((_, s) => (
                      <Star key={s} className="w-3.5 h-3.5 fill-sienna text-sienna" />
                    ))}
                  </span>
                )}
                <span className="block text-sm font-semibold text-bone">{t.name}</span>
              </figcaption>
            </figure>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const About = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [40, -40]);

  return (
    <section ref={ref} className="py-24 md:py-32 bg-coal border-y border-white/10 overflow-hidden">
      <div className="max-w-6xl mx-auto px-6 md:px-10 grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
        <Reveal className="md:col-span-5">
          <div className="relative border border-white/10 overflow-hidden aspect-[4/5]">
            <motion.img
              style={{ y: imgY, scale: 1.12 }}
              src="/persephone.png"
              alt="Persephone Dennis"
              className="w-full h-full object-cover object-[center_15%]"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-obsidian/60 via-transparent to-transparent" />
          </div>
        </Reveal>
        <div className="md:col-span-7">
          <Reveal>
            <Overline>Your coach</Overline>
            <h2 className="font-display font-light text-4xl md:text-5xl leading-tight text-bone">
              I'm Persephone Dennis. I teach identity transformation.
            </h2>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="mt-8 space-y-6 text-base md:text-lg leading-relaxed text-ash">
              <p>
                My work sits at the meeting point of identity, the subconscious mind,
                neuroplasticity, human behaviour and emotional awareness. I have one core
                conviction, and everything I teach flows from it: lasting change never comes from
                changing behaviour alone. It comes from becoming a different person. When identity
                changes, behaviour follows.
              </p>
              <p>
                The Reset is where I introduce you to that way of working: live, personally, in a
                small group. For those who want to go much deeper, it also opens the door to
                Source, my six-month identity transformation programme. But nothing about this
                page assumes that. Six weeks, done properly, stands on its own.
              </p>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const FAQS = [
  { q: "When does it start, and how does it run?", a: "We meet live every Monday at 8:30pm UK time (3:30pm EST) for six weeks, beginning Monday 24th August. We gather as a small group and sessions are interactive, so expect to be coached, not talked at. Every session is recorded in case life gets in the way." },
  { q: "What if I can't make a session live?", a: "Every session is recorded and shared with the group, so you can watch in your own time. That said, the live room is where the movement happens, so I'd encourage you to protect the hour wherever you can." },
  { q: "How big is the group?", a: "Deliberately small. Large enough that you benefit from witnessing other people being coached, which is often where the deepest realisations land, and small enough that you are seen, known and personally coached by me." },
  { q: "How is this different from Source?", a: "Source is my six-month identity transformation programme, the deep, sustained work. The Reset is six weeks: a complete experience in itself, and the most honest way to discover whether my methodology is the right fit for you before ever considering something longer." },
  { q: "Is there a payment plan?", a: "Yes. You can pay £444 in full, or two monthly payments of £222. Both options include exactly the same experience." },
  { q: "How do I claim the complimentary 1:1?", a: "Simply join by the end of Tuesday 11th August. Everyone enrolled by then receives a private hour with me during the six weeks. We'll arrange it together once you're inside." },
  { q: "What happens after I join?", a: "You'll receive a welcome email from me with everything you need: the schedule, how to join our sessions, and how to prepare. Nothing complicated. Just come as you are, and be willing." },
];

const FaqItem = ({ q, a, index }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-b border-white/10">
      <button
        data-testid={`faq-question-${index}`}
        onClick={() => setOpen(!open)}
        aria-expanded={open}
        className="w-full flex items-center justify-between gap-6 py-7 text-left group focus:outline-none focus-visible:ring-2 focus-visible:ring-sienna"
      >
        <span className="font-display font-light text-xl md:text-2xl text-bone group-hover:text-sienna transition-colors duration-300">
          {q}
        </span>
        <span className="shrink-0 text-ash group-hover:text-sienna transition-colors duration-300">
          {open ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.45, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="pb-7 pr-10 text-base leading-relaxed text-ash max-w-2xl">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

const Faq = () => (
  <section className="py-24 md:py-32">
    <div className="max-w-3xl mx-auto px-6 md:px-10">
      <Reveal>
        <Overline>Questions</Overline>
        <h2 className="font-display font-light text-4xl md:text-5xl leading-tight text-bone mb-14">
          Everything you'd want to ask me.
        </h2>
      </Reveal>
      <div className="border-t border-white/10">
        {FAQS.map((f, i) => (
          <FaqItem key={i} q={f.q} a={f.a} index={i} />
        ))}
      </div>
    </div>
  </section>
);

const EmailSignup = () => {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");

  const submit = async (e) => {
    e.preventDefault();
    if (!email || state === "sending") return;
    setState("sending");
    try {
      const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      if (!res.ok) throw new Error("subscribe failed");
      setState("done");
    } catch {
      setState("error");
    }
  };

  return (
    <section className="py-24 md:py-32 bg-coal border-y border-white/10">
      <div className="max-w-3xl mx-auto px-6 md:px-10">
        <Reveal>
          <Overline>Not ready today?</Overline>
          <h2 className="font-display font-light text-4xl md:text-5xl leading-tight text-bone">
            That's alright. Stay close.
          </h2>
          <p className="mt-6 max-w-xl text-base leading-relaxed text-ash">
            If now isn't the moment, leave your email and I'll write to you gently, and only
            when it matters. No noise, no pressure. Just an honest note now and then, and a
            reminder before the door closes on my birthday.
          </p>
        </Reveal>
        <Reveal delay={0.15}>
          {state === "done" ? (
            <p data-testid="signup-success-message" className="mt-10 font-display italic font-light text-2xl text-sienna">
              Thank you, you're on the list. I'll be in touch.
            </p>
          ) : (
            <form onSubmit={submit} className="mt-10 flex flex-col sm:flex-row gap-4 max-w-xl">
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Your email address"
                data-testid="signup-email-input"
                className="flex-1 bg-obsidian border border-white/15 px-6 py-4 text-base text-bone placeholder:text-ash/50 focus:outline-none focus:border-sienna transition-colors duration-300"
              />
              <motion.button
                type="submit"
                data-testid="signup-submit-button"
                disabled={state === "sending"}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="inline-flex items-center justify-center gap-3 bg-sienna text-bone px-8 py-4 text-sm tracking-[0.12em] uppercase font-semibold hover:bg-ember transition-colors duration-300 disabled:opacity-60"
              >
                {state === "sending" ? "One moment..." : "Keep me close"}
              </motion.button>
            </form>
          )}
          {state === "error" && (
            <p data-testid="signup-error-message" className="mt-4 text-sm text-sienna">
              Something didn't go through. Please try again.
            </p>
          )}
        </Reveal>
      </div>
    </section>
  );
};

const FinalInvitation = () => (
  <section className="relative py-32 md:py-44 border-t border-white/10 overflow-hidden">
    <div className="bonus-glow absolute inset-0" />
    <div className="relative max-w-3xl mx-auto px-6 md:px-10 text-center">
      <Reveal>
        <h2 className="font-display font-light text-4xl md:text-6xl leading-tight text-bone">
          If something in you recognised itself while reading this page, I would like to work with you.
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <p className="mt-10 text-base md:text-lg leading-relaxed text-ash max-w-xl mx-auto">
          Not the version of you that has it all figured out. That person doesn't need me. The
          version that is tired of knowing and ready to start becoming. Six weeks. A small room.
          Real coaching. That is the whole offer, and I mean every word of it.
        </p>
      </Reveal>
      <Reveal delay={0.25}>
        <div className="mt-12">
          <PayButtons align="center" idPrefix="final" />
          <p className="mt-6 text-xs tracking-[0.15em] uppercase text-ash/70">
            Join by Tuesday 11th August and a private 1:1 is yours, my birthday gift
          </p>
          <div className="mt-7 flex justify-center">
            <BookCallLink idPrefix="final" />
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

const Footer = () => (
  <footer className="border-t border-white/10 py-10">
    <div className="max-w-6xl mx-auto px-6 md:px-10 flex flex-col md:flex-row items-center justify-between gap-4">
      <span className="font-display text-lg text-bone">Persephone Dennis</span>
      <span className="text-xs tracking-[0.2em] uppercase text-ash/60">
        The Reset · Six weeks of live coaching · © 2026
      </span>
    </div>
  </footer>
);

const useLenis = () => {
  useEffect(() => {
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    let rafId;
    const raf = (time) => {
      lenis.raf(time);
      rafId = requestAnimationFrame(raf);
    };
    rafId = requestAnimationFrame(raf);
    return () => {
      cancelAnimationFrame(rafId);
      lenis.destroy();
    };
  }, []);
};

const ThankYou = () => {
  useEffect(() => {
    track("pageview", "/thank-you");
  }, []);

  return (
  <div className="App bg-obsidian text-bone font-body min-h-screen flex flex-col">
    <div className="noise-overlay" aria-hidden="true" />
    <div className="bonus-glow fixed inset-0 pointer-events-none" />
    <Nav />
    <main data-testid="thank-you-page" className="relative flex-1 flex items-center justify-center px-6 py-32">
      <div className="max-w-2xl text-center">
        <Reveal>
          <div className="flex items-center justify-center gap-4 mb-8">
            <span className="h-px w-10 bg-sienna" />
            <span className="text-xs tracking-[0.28em] uppercase font-medium text-sienna">You're in</span>
            <span className="h-px w-10 bg-sienna" />
          </div>
          <h1 className="font-display font-light text-4xl md:text-6xl leading-tight text-bone">
            Welcome to The Reset.
          </h1>
          <p className="mt-8 text-base md:text-lg leading-relaxed text-ash">
            Your place is booked, and I'm genuinely glad you're here. A welcome email is on its way
            with everything you need: how to join our sessions and how to prepare. We meet live
            every Monday at 8:30pm UK time (3:30pm EST), beginning Monday 24th August.
          </p>
          <p className="mt-6 text-base leading-relaxed text-ash">
            Until then, there is nothing to do and nothing to prove. Just notice what stirred in
            you when you decided. That is where we begin.
          </p>
          <p className="mt-8 font-display italic font-light text-xl md:text-2xl text-bone/85">
            With you in it, the room is already better.
          </p>
          <div className="mt-12 flex flex-col sm:flex-row items-center justify-center gap-8">
            <a
              href={CALENDLY_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="thank-you-book-call-link"
              className="link-underline text-sm text-bone hover:text-sienna transition-colors duration-300"
            >
              Questions before we begin? Book a call with me
            </a>
            <Link
              to="/"
              data-testid="thank-you-home-link"
              className="link-underline text-sm text-ash hover:text-bone transition-colors duration-300"
            >
              Back to the page
            </Link>
          </div>
        </Reveal>
      </div>
    </main>
    <Footer />
  </div>
  );
};

const SignupsPage = () => {
  const [input, setInput] = useState("");
  const [subs, setSubs] = useState(null);
  const [stats, setStats] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = async (k) => {
    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/subscribers?key=${encodeURIComponent(k)}`
      );
      if (!res.ok) throw new Error();
      const data = await res.json();
      setSubs(data.subscribers);
      const statsRes = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/stats?key=${encodeURIComponent(k)}`
      );
      if (statsRes.ok) {
        const statsData = await statsRes.json();
        setStats(statsData.counts);
      }
      sessionStorage.setItem("the_list_key", k);
      setError(false);
    } catch {
      setError(true);
      setSubs(null);
      sessionStorage.removeItem("the_list_key");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const k = sessionStorage.getItem("the_list_key");
    if (k) load(k);
  }, []);

  const submit = (e) => {
    e.preventDefault();
    if (input) load(input);
  };

  return (
    <div className="App bg-obsidian text-bone font-body min-h-screen flex flex-col">
      <div className="noise-overlay" aria-hidden="true" />
      <Nav />
      <main data-testid="signups-page" className="flex-1 w-full max-w-2xl mx-auto px-6 pt-40 pb-24">
        <Overline>Just for you</Overline>
        <h1 className="font-display font-light text-4xl md:text-5xl text-bone">The list.</h1>
        {subs === null ? (
          <>
            <p className="mt-6 text-base leading-relaxed text-ash">
              Everyone who has asked to stay close lives behind this door.
            </p>
            <form onSubmit={submit} className="mt-8 flex flex-col sm:flex-row gap-4">
              <input
                type="password"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Your quiet word"
                data-testid="list-passcode-input"
                className="flex-1 bg-coal border border-white/15 px-6 py-4 text-base text-bone placeholder:text-ash/50 focus:outline-none focus:border-sienna transition-colors duration-300"
              />
              <button
                type="submit"
                disabled={loading}
                data-testid="list-unlock-button"
                className="inline-flex items-center justify-center gap-3 bg-sienna text-bone px-8 py-4 text-sm tracking-[0.12em] uppercase font-semibold hover:bg-ember transition-colors duration-300 disabled:opacity-60"
              >
                {loading ? "Opening..." : "Open the list"}
              </button>
            </form>
            {error && (
              <p data-testid="list-error-message" className="mt-4 text-sm text-sienna">
                That word didn't open the door.
              </p>
            )}
          </>
        ) : (
          <>
            <p data-testid="list-count" className="mt-6 text-base text-ash">
              {subs.length === 0
                ? "No one here yet. When someone leaves their email, they will appear below."
                : `${subs.length} ${subs.length === 1 ? "person" : "people"} staying close.`}
            </p>
            <ul className="mt-8 border-b border-white/10">
              {subs.map((s, i) => (
                <li
                  key={i}
                  data-testid={`subscriber-row-${i}`}
                  className="flex items-center justify-between gap-6 py-4 border-t border-white/10"
                >
                  <span className="text-base text-bone">{s.email}</span>
                  <span className="text-sm text-ash shrink-0">
                    {s.subscribed_at
                      ? new Date(s.subscribed_at).toLocaleDateString("en-GB", {
                          day: "numeric",
                          month: "short",
                        })
                      : ""}
                  </span>
                </li>
              ))}
            </ul>
            <button
              onClick={() => load(sessionStorage.getItem("the_list_key"))}
              data-testid="list-refresh-button"
              className="link-underline mt-8 text-sm text-ash hover:text-bone transition-colors duration-300"
            >
              Refresh the list
            </button>
            {stats && (
              <div data-testid="pulse-section" className="mt-16">
                <h2 className="font-display font-light text-2xl md:text-3xl text-bone">The pulse.</h2>
                <p className="mt-3 text-sm text-ash">
                  How many people have visited, and what they pressed. All anonymous.
                </p>
                <ul className="mt-6 border-b border-white/10">
                  {Object.entries(PULSE_LABELS).map(([key, label]) => (
                    <li
                      key={key}
                      data-testid={`pulse-row-${key}`}
                      className="flex items-center justify-between gap-6 py-3 border-t border-white/10"
                    >
                      <span className="text-sm text-bone/90">{label}</span>
                      <span className="text-sm text-sienna font-semibold">{stats[key] || 0}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </main>
      <Footer />
    </div>
  );
};

const SalesPage = () => {
  useEffect(() => {
    track("pageview", "/");
  }, []);

  return (
    <div className="App bg-obsidian text-bone font-body overflow-x-clip">
      <div className="noise-overlay" aria-hidden="true" />
      <Nav />
      <main data-testid="sales-page">
        <Hero />
        <Opening />
        <Problem />
        <Marquee />
        <Manifesto />
        <Weeks />
        <WalkAway />
        <WhoFor />
        <Pricing />
        <Bonus />
        <Testimonials />
        <About />
        <Faq />
        <EmailSignup />
        <FinalInvitation />
      </main>
      <Footer />
    </div>
  );
};

function App() {
  useLenis();
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/" element={<SalesPage />} />
        <Route path="/thank-you" element={<ThankYou />} />
        <Route path="/the-list" element={<SignupsPage />} />
      </Routes>
    </BrowserRouter>
  );
}

export default App;
